import React, { useState, useEffect } from "react";
import { Popover, Select, message } from "antd";
import {
  Zap,
  HardDrive,
  RefreshCw,
  FolderOpen,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import moment from "moment";
import { getAuthMethod, postAuthMethod } from "../../services/HttpServices";
import MediaManagerModal from "./MediaManagerModal";
import { WhatsAppLimitsModal } from "./WhatsAppAccountHealthBadge";

const WhatsAppAccountHeaderPill = ({
  phoneNumbers = [],
  onSelectPhone,
  onSyncComplete,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [localPhones, setLocalPhones] = useState(phoneNumbers);
  const [loadingSync, setLoadingSync] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [isLimitsModalOpen, setIsLimitsModalOpen] = useState(false);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);

  // Storage State
  const [storageQuota, setStorageQuota] = useState(null);

  // Initial phone ID resolution with strict type matching
  const getInitialPhoneId = () => {
    try {
      const saved = localStorage.getItem("whatsapp_selected_phone_id");
      if (saved && phoneNumbers.some((p) => String(p.id) === String(saved))) {
        const found = phoneNumbers.find((p) => String(p.id) === String(saved));
        return found ? found.id : saved;
      }
    } catch (e) {}
    return phoneNumbers.length > 0 ? phoneNumbers[0].id : null;
  };

  const [selectedPhoneId, setSelectedPhoneId] = useState(getInitialPhoneId);

  // Synchronize when phoneNumbers prop changes
  useEffect(() => {
    setLocalPhones(phoneNumbers);
    if (phoneNumbers && phoneNumbers.length > 0) {
      setSelectedPhoneId((prev) => {
        if (prev && phoneNumbers.some((p) => String(p.id) === String(prev))) {
          const found = phoneNumbers.find((p) => String(p.id) === String(prev));
          return found ? found.id : prev;
        }
        try {
          const saved = localStorage.getItem("whatsapp_selected_phone_id");
          if (saved && phoneNumbers.some((p) => String(p.id) === String(saved))) {
            const found = phoneNumbers.find((p) => String(p.id) === String(saved));
            return found ? found.id : saved;
          }
        } catch (e) {}
        return phoneNumbers[0].id;
      });
    }
  }, [phoneNumbers]);

  // Fetch Media Storage Quota
  useEffect(() => {
    fetchStorageQuota();
  }, []);

  const fetchStorageQuota = async () => {
    try {
      const res = await getAuthMethod("/api/media/quota");
      if (res && res.data) {
        setStorageQuota(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch media quota", error);
    }
  };

  const handlePhoneChange = (val) => {
    const matched =
      phoneNumbers.find((p) => String(p.id) === String(val)) ||
      displayPhones.find((p) => String(p.id) === String(val));
    const finalId = matched ? matched.id : val;
    setSelectedPhoneId(finalId);
    const wabaId =
      matched?.wabaId ||
      matched?.whatsappBusinessAccountId ||
      1;
    try {
      localStorage.setItem("whatsapp_selected_phone_id", String(finalId));
      if (wabaId) {
        localStorage.setItem("whatsapp_selected_waba_id", String(wabaId));
      }
    } catch (e) {}
    if (onSelectPhone) {
      onSelectPhone(finalId, wabaId);
    }
  };

  const displayPhones =
    localPhones && localPhones.length > 0
      ? localPhones
      : [
          {
            id: "default",
            displayPhoneNumber:
              phoneNumbers?.[0]?.displayPhoneNumber || "+91 98343 75...",
            qualityRating: "RED",
            quotaSnapshot: { estimatedOpened: 0, tierLimit: 2000 },
          },
        ];

  const activePhone =
    displayPhones.find((p) => String(p.id) === String(selectedPhoneId)) ||
    displayPhones[0];
  const quota = activePhone?.quotaSnapshot || {};

  // Sync Status with Meta
  const handleSync = async () => {
    if (!activePhone?.phoneNumberId || cooldown > 0) return;

    setLoadingSync(true);
    try {
      const response = await postAuthMethod(
        `/api/whatsapp/accounts/phone-numbers/${activePhone.phoneNumberId}/sync-status`
      );
      const updatedPhone = response.data;

      const newPhones = displayPhones.map((p) =>
        p.id === updatedPhone.id ? { ...p, ...updatedPhone } : p
      );
      setLocalPhones(newPhones);
      message.success("Account quality and limits synced from Meta");

      if (onSyncComplete) {
        onSyncComplete(newPhones);
      }

      setCooldown(15);
      const timer = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Failed to sync phone number status", error);
      message.error("Failed to sync data from Meta. Try again later.");
    } finally {
      setLoadingSync(false);
    }
  };

  const formatLimit = (limit) => {
    if (limit === null || limit === undefined) return "2,000";
    if (limit > 1000000) return "Unlimited";
    return limit.toLocaleString();
  };

  // Messaging Tier Metrics
  const estimatedOpened = quota.estimatedOpened || 0;
  const tierLimit = quota.tierLimit || 2000;
  const percentUsed =
    tierLimit > 0
      ? Math.min(100, Math.round((estimatedOpened / tierLimit) * 100))
      : 0;
  const isDanger = percentUsed >= 95;
  const isWarning = percentUsed >= 80 && percentUsed < 95;

  // Quality Rating Badge
  const rawQuality = (
    quota.quality ||
    activePhone?.qualityRating ||
    "RED"
  ).toUpperCase();

  const isHighQuality =
    rawQuality.includes("GREEN") || rawQuality.includes("HIGH");
  const isMediumQuality =
    rawQuality.includes("YELLOW") || rawQuality.includes("MEDIUM");

  // Media Storage Metrics
  const usedBytes = storageQuota?.usedBytes || 0;
  const totalLimitBytes = storageQuota?.totalLimitBytes || 524288000; // default 500MB
  const usedMB = (usedBytes / (1024 * 1024)).toFixed(1);
  const limitMB = (totalLimitBytes / (1024 * 1024)).toFixed(1);
  const freeMB = Math.max(0, (limitMB - usedMB)).toFixed(1);
  const storagePercent =
    totalLimitBytes > 0 ? (usedBytes / totalLimitBytes) * 100 : 0;
  const isStorageWarning = storagePercent >= 85;

  // Popover Dropdown Card Content
  const popoverContent = (
    <div className="tw-w-[340px] tw-font-inter tw-p-1">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-pb-1">
        <div>
          <div className="tw-text-xs tw-font-bold tw-text-slate-800">
            WhatsApp Account Quotas
          </div>
          <div className="tw-text-[11px] tw-text-slate-400">
            Meta Cloud API Status & Limits
          </div>
        </div>
        <span
          className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-bold ${
            isHighQuality
              ? "tw-bg-emerald-50 tw-text-emerald-700"
              : isMediumQuality
              ? "tw-bg-amber-50 tw-text-amber-700"
              : "tw-bg-rose-50 tw-text-rose-700"
          }`}
        >
          {isHighQuality ? (
            <CheckCircle2 size={11} className="tw-text-emerald-500" />
          ) : isMediumQuality ? (
            <AlertTriangle size={11} className="tw-text-amber-500" />
          ) : (
            <AlertCircle size={11} className="tw-text-rose-500" />
          )}
          <span>
            {isHighQuality
              ? "High Quality"
              : isMediumQuality
              ? "Medium Quality"
              : "Poor Quality"}
          </span>
        </span>
      </div>

      <div className="tw-h-px tw-w-full tw-bg-slate-100 tw-my-3" />

      {/* Section 1: Phone Selector */}
      <div className="tw-mb-3.5">
        <div className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-mb-1.5">
          Sender Phone Number
        </div>
        {displayPhones.length > 1 ? (
          <Select
            value={activePhone?.id ?? (selectedPhoneId || displayPhones[0].id)}
            onChange={handlePhoneChange}
            style={{ width: "100%" }}
            size="small"
            bordered={false}
            className="!tw-bg-slate-50 !tw-rounded-xl tw-border tw-border-solid tw-border-slate-200/80 tw-font-medium tw-text-xs [&_.ant-select-selector]:!tw-rounded-xl [&_.ant-select-selector]:!tw-px-3 [&_.ant-select-selector]:!tw-py-1 [&_.ant-select-selection-item]:!tw-font-bold [&_.ant-select-selection-item]:!tw-text-slate-800"
            options={displayPhones.map((phone) => ({
              value: phone.id,
              label:
                phone.displayPhoneNumber ||
                phone.phoneNumber ||
                String(phone.id),
            }))}
          />
        ) : (
          <div className="tw-px-3 tw-py-1.5 tw-rounded-xl tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-200/80 tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-xs tw-font-bold tw-text-slate-800">
              {activePhone?.displayPhoneNumber ||
                activePhone?.phoneNumber ||
                "+91 98343 75819"}
            </span>
            <span className="tw-text-[10px] tw-text-emerald-600 tw-font-semibold">
              Verified
            </span>
          </div>
        )}
      </div>

      {/* Section 2: 24h Messaging Tier & Limit */}
      <div className="tw-mb-3.5 tw-p-3 tw-rounded-xl tw-bg-slate-50/70 tw-border tw-border-solid tw-border-slate-100">
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <Zap size={13} className="tw-text-[#2E90FA]" />
            <span className="tw-text-[11px] tw-font-bold tw-text-slate-700">
              24h Messaging Limit
            </span>
          </div>
          <span className="tw-text-[10px] tw-font-bold tw-text-blue-600">
            Tier {quota.tier || "1"} ({formatLimit(tierLimit)}/day)
          </span>
        </div>

        <div className="tw-flex tw-items-baseline tw-justify-between tw-text-xs tw-mb-1.5">
          <span className="tw-font-bold tw-text-slate-800">
            {estimatedOpened.toLocaleString()}{" "}
            <span className="tw-text-slate-400 tw-font-normal">
              / {formatLimit(tierLimit)} used
            </span>
          </span>
          <span
            className={`tw-font-bold text-[11px] ${
              isDanger
                ? "tw-text-rose-600"
                : isWarning
                ? "tw-text-amber-600"
                : "tw-text-slate-500"
            }`}
          >
            {percentUsed}%
          </span>
        </div>

        <div className="tw-w-full tw-bg-slate-200/80 tw-rounded-full tw-h-1.5 tw-overflow-hidden tw-mb-2">
          <div
            className={`tw-h-full tw-rounded-full tw-transition-all tw-duration-500 ${
              isDanger
                ? "tw-bg-rose-500"
                : isWarning
                ? "tw-bg-amber-500"
                : "tw-bg-[#2E90FA]"
            }`}
            style={{ width: `${Math.max(3, percentUsed)}%` }}
          />
        </div>

        <div className="tw-flex tw-items-center tw-justify-between tw-text-[10px] tw-text-slate-400 tw-pt-1">
          <span>
            {quota.lastSynced ? moment(quota.lastSynced).fromNow() : "Just now"}
          </span>
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              type="button"
              onClick={handleSync}
              disabled={loadingSync || cooldown > 0}
              className="tw-border-0 tw-bg-transparent tw-text-blue-600 hover:tw-text-blue-700 tw-font-semibold tw-cursor-pointer tw-p-0 tw-flex tw-items-center tw-gap-1 disabled:tw-opacity-50"
            >
              <RefreshCw
                size={10}
                className={loadingSync ? "tw-animate-spin" : ""}
              />
              <span>{cooldown > 0 ? `${cooldown}s` : "Sync"}</span>
            </button>
            <span>•</span>
            <button
              type="button"
              onClick={() => setIsLimitsModalOpen(true)}
              className="tw-border-0 tw-bg-transparent tw-text-[#5D44FF] hover:tw-underline tw-font-semibold tw-cursor-pointer tw-p-0"
            >
              Limits Info
            </button>
          </div>
        </div>
      </div>

      {/* Section 3: Media Storage */}
      <div className="tw-p-3 tw-rounded-xl tw-bg-slate-50/70 tw-border tw-border-solid tw-border-slate-100">
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <HardDrive size={13} className="tw-text-[#7C3AED]" />
            <span className="tw-text-[11px] tw-font-bold tw-text-slate-700">
              Media Storage
            </span>
          </div>
          <span className="tw-text-[10px] tw-font-bold tw-text-purple-600">
            {freeMB} MB Free
          </span>
        </div>

        <div className="tw-flex tw-items-baseline tw-justify-between tw-text-xs tw-mb-1.5">
          <span className="tw-font-bold tw-text-slate-800">
            {usedMB} MB{" "}
            <span className="tw-text-slate-400 tw-font-normal">
              / {limitMB} MB
            </span>
          </span>
          <span className="tw-font-bold text-[11px] tw-text-slate-500">
            {storagePercent.toFixed(0)}%
          </span>
        </div>

        <div className="tw-w-full tw-bg-slate-200/80 tw-rounded-full tw-h-1.5 tw-overflow-hidden tw-mb-2">
          <div
            className={`tw-h-full tw-rounded-full tw-transition-all tw-duration-500 ${
              storagePercent >= 100
                ? "tw-bg-rose-500"
                : isStorageWarning
                ? "tw-bg-amber-500"
                : "tw-bg-[#7C3AED]"
            }`}
            style={{
              width: `${Math.max(3, Math.min(100, storagePercent))}%`,
            }}
          />
        </div>

        <div className="tw-flex tw-items-center tw-justify-end tw-pt-1">
          <button
            type="button"
            onClick={() => setIsMediaModalOpen(true)}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-lg tw-bg-white tw-border tw-border-solid tw-border-slate-200 hover:tw-bg-slate-50 tw-text-slate-700 hover:tw-text-[#5D44FF] tw-font-semibold tw-text-[10px] tw-transition-colors tw-cursor-pointer"
          >
            <FolderOpen size={11} />
            <span>Manage Files</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Popover
        content={popoverContent}
        trigger="click"
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        placement="bottomRight"
        arrow={{ pointAtCenter: true }}
      >
        <div
          role="button"
          tabIndex={0}
          className="tw-inline-flex tw-items-center tw-gap-2.5 tw-h-[38px] tw-px-3.5 tw-bg-white tw-border tw-border-solid tw-border-slate-200/90 hover:tw-border-[#5D44FF]/50 tw-rounded-xl tw-shadow-xs tw-font-inter tw-cursor-pointer tw-transition-all select-none"
        >
          {/* Active phone number with pulse indicator */}
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <span className="tw-size-2 tw-rounded-full tw-bg-emerald-500 tw-animate-pulse tw-shrink-0" />
            <span className="tw-text-xs tw-font-bold tw-text-slate-800">
              {activePhone?.displayPhoneNumber ||
                activePhone?.phoneNumber ||
                "+91 98343 75819"}
            </span>
          </div>

          <span className="tw-w-px tw-h-3.5 tw-bg-slate-200" />

          {/* Compact Quality Tag */}
          <span
            className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-1.5 tw-py-0.5 tw-rounded-md tw-text-[10px] tw-font-bold ${
              isHighQuality
                ? "tw-bg-emerald-50 tw-text-emerald-700"
                : isMediumQuality
                ? "tw-bg-amber-50 tw-text-amber-700"
                : "tw-bg-rose-50 tw-text-rose-700"
            }`}
          >
            {isHighQuality ? (
              <CheckCircle2 size={10} className="tw-text-emerald-500" />
            ) : isMediumQuality ? (
              <AlertTriangle size={10} className="tw-text-amber-500" />
            ) : (
              <AlertCircle size={10} className="tw-text-rose-500" />
            )}
            <span>{isHighQuality ? "High Quality" : "Medium"}</span>
          </span>

          <span className="tw-w-px tw-h-3.5 tw-bg-slate-200 tw-hidden sm:tw-inline-block" />

          {/* Compact Storage tag */}
          <div className="tw-hidden sm:tw-flex tw-items-center tw-gap-1.5 tw-text-slate-500 tw-text-[11px] tw-font-semibold">
            <HardDrive size={12} className="tw-text-slate-400" />
            <span>{usedMB}MB</span>
          </div>

          <ChevronDown
            size={13}
            className={`tw-text-slate-400 tw-transition-transform tw-duration-200 ${
              popoverOpen ? "tw-rotate-180" : ""
            }`}
          />
        </div>
      </Popover>

      {/* Modals */}
      <WhatsAppLimitsModal
        open={isLimitsModalOpen}
        onCancel={() => setIsLimitsModalOpen(false)}
      />

      {isMediaModalOpen && (
        <MediaManagerModal
          visible={isMediaModalOpen}
          onClose={() => {
            setIsMediaModalOpen(false);
            fetchStorageQuota();
          }}
        />
      )}
    </>
  );
};

export default WhatsAppAccountHeaderPill;
